import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "~/api/provider";

/**
 * BRONCO Opus 02 — the thinker economy's RPC routes (`thinkers.*`, `{ok, data}`), typed for the Thinkers page. They ride the
 * ApiClient's bearer lane (`research.post`): a raw fetch would arrive anonymous. Every decision is the server's — these types
 * only carry what it said.
 */

export type GrantLimits = {
  priorityCeiling?: number | null;
  slots?: number | null;
  tokensPerDay?: number | null;
  hours?: { dow: number; from: string; to: string }[] | null;
  dataClass?: string | null;
  acts?: string[] | null;
};

export type ThinkerRecord = {
  thinkerId: string;
  label: string;
  kind: string;
  ownerTeamId: string;
  ownerTeamName: string;
  pooled: boolean;
  poolLimits: GrantLimits | null;
  section: "mine" | "shared" | "pooled";
  hubs: { name: string; live: boolean; slots: number }[];
  capability: {
    maxContextTokens: number | null;
    recorded: boolean;
    unknown: string[];
    source: string | null;
  };
  myGrant: {
    source: string;
    grantId: string | null;
    mode: string;
    rights: string[];
    limits: GrantLimits;
    expiresAt: string | null;
  } | null;
  tokensToday: number;
  requestsToday: number;
  grantsOut: number;
};

export type GrantRow = {
  id: string;
  thinkerId: string;
  label: string;
  mode: string;
  status: string;
  subjectKind: string;
  ownerTeamId: string;
  ownerTeamName: string;
  recipientTeamId: string | null;
  recipientTeamName: string | null;
  recipientUserId: string | null;
  recipientUserName: string | null;
  recipientProjectId: string | null;
  recipientRole: string | null;
  limits: GrantLimits;
  startsAt: string | null;
  expiresAt: string | null;
  lastUsedAt: string | null;
  purpose: string | null;
  requestedHours: number | null;
  widenedFrom: string | null;
  tokensToday: number;
  createdAt: string;
};

export type KeyRow = {
  id: string;
  provider: string;
  baseUrl: string | null;
  label: string;
  masked: string;
  models: string[];
  thinkers: string[];
  status: string;
  createdAt: string;
  lastUsedAt: string | null;
};

export type FallbackHop = {
  thinkerId?: string | null;
  capability?: string | null;
  mode: "auto" | "ask" | "never";
  maxUsdPerDay?: number | null;
  requireLocal?: boolean;
};

export type ChainRow = {
  id: string;
  scope: string;
  scopeId: string;
  chain: FallbackHop[];
  labels: (string | null)[];
  updatedAt: string;
};

export type FallbackView = {
  chains: ChainRow[];
  policy: { neverLeaveEstate: boolean; poolOptIn: boolean; defaultChainId: string | null };
};

export type UsageView = {
  days: number;
  mine: {
    day: string;
    thinkerId: string;
    label: string;
    grantMode: string;
    requests: number;
    tokens: number;
  }[];
  lent: {
    grantId: string;
    day: string;
    thinkerId: string;
    mode: string;
    recipient: string | null;
    requests: number;
    tokens: number;
  }[];
};

type Envelope<T> = { ok: boolean; data: T; message?: string; error?: string };

function useRpc() {
  const api = useApi();
  return async <T>(path: string, body: unknown = {}): Promise<T> => {
    const response = (await api.research.post(path, body)) as Envelope<T>;
    if (!response.ok) {
      throw new Error(response.message ?? response.error ?? path);
    }
    return response.data;
  };
}

const KEY = ["thinkers", "economy"] as const;

export function useThinkerRegistry() {
  const rpc = useRpc();
  return useQuery({
    queryKey: [...KEY, "registry"],
    queryFn: async () => (await rpc<{ thinkers: ThinkerRecord[] }>("thinkers.registry")).thinkers,
    staleTime: 15_000,
  });
}

export function useGrants(as: "owner" | "recipient") {
  const rpc = useRpc();
  return useQuery({
    queryKey: [...KEY, "grants", as],
    queryFn: async () => (await rpc<{ grants: GrantRow[] }>("thinkers.grants.list", { as })).grants,
    staleTime: 15_000,
  });
}

export function useProviderKeys() {
  const rpc = useRpc();
  return useQuery({
    queryKey: [...KEY, "keys"],
    queryFn: () =>
      rpc<{ keys: KeyRow[]; plan: { name: string; allowsByoKey: boolean } }>("thinkers.keys.list"),
  });
}

export function useFallbacks() {
  const rpc = useRpc();
  return useQuery({
    queryKey: [...KEY, "fallbacks"],
    queryFn: () => rpc<FallbackView>("thinkers.fallbacks.get"),
  });
}

export function useThinkerUsage(days: number) {
  const rpc = useRpc();
  return useQuery({
    queryKey: [...KEY, "usage", days],
    queryFn: () => rpc<UsageView>("thinkers.usage", { days }),
  });
}

/** One economy write; every write refreshes the whole page (the registry, grants and usage move together). */
export function useEconomyAction<TBody>(path: string) {
  const rpc = useRpc();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: TBody) => rpc<unknown>(path, body),
    onSuccess: () => client.invalidateQueries({ queryKey: KEY }),
  });
}

/** "priority ≤ 5 · 2 at once · 2,000,000 tokens/day · internal data" — the limits a person reads. */
export function describeLimits(
  limits: GrantLimits | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (!limits) {
    return t("no limits");
  }
  const parts: string[] = [];
  if (limits.priorityCeiling != null)
    parts.push(t("priority ≤ {{n}}", { n: limits.priorityCeiling }));
  if (limits.slots != null) parts.push(t("{{n}} at once", { n: limits.slots }));
  if (limits.tokensPerDay != null)
    parts.push(t("{{n}} tokens/day", { n: limits.tokensPerDay.toLocaleString() }));
  if (limits.dataClass) parts.push(t("{{class}} data", { class: t(limits.dataClass) }));
  if (limits.hours?.length) parts.push(t("set hours"));
  return parts.length > 0 ? parts.join(" · ") : t("no limits");
}
